import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight, ExternalLink, Search, Crosshair, Loader2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PolicyBar } from "@/components/PolicyBar";
import { MEDIA } from "@/lib/media";

const SPEC_ROWS = [
  ["caliber", "Caliber / chambering"], ["action", "Action"], ["barrel_length", "Barrel length"],
  ["overall_length", "Overall length"], ["weight", "Weight"], ["capacity", "Capacity"],
  ["twist_rate", "Twist rate"], ["stock", "Stock"], ["optic_mounting", "Optic mounting"], ["msrp", "MSRP"],
];

export function FirearmsResearch() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      api.publicFirearms({ category: cat || undefined, q: q || undefined }).then(setData).catch(() => setData({ firearms: [], categories: [] }));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cat, q]);

  const list = data?.firearms || [];

  return (
    <div className="bg-paper min-h-screen" data-testid="firearms-page">
      <SiteHeader />
      <section className="relative bg-charcoal text-bone overflow-hidden grain" data-testid="firearms-hero">
        <div className="absolute inset-0 z-0">
          <img src={MEDIA.firearmsBand} alt="Bolt-action hunting rifle on rock" className="w-full h-full object-cover object-right opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/30" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-10 py-16 lg:py-20 max-w-3xl">
          <nav className="flex items-center gap-1.5 text-xs text-bone/50 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} />
            <Link to="/gear" className="hover:text-blaze">Gear</Link><ChevronRight size={13} />
            <span className="text-bone font-medium">Firearms Research</span>
          </nav>
          <span className="hr-overline text-blaze">Firearms Research</span>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[0.9] tracking-tight mt-3">Research with confidence.</h1>
          <p className="text-lg text-bone/80 mt-5 max-w-xl">Detailed specifications, manufacturer information, fit-for-hunt insights, and applicable regulations — research only. HomeRange never processes firearm sales.</p>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-12">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button onClick={() => setCat("")} className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${cat === "" ? "bg-blaze text-white border-blaze" : "border-saddle/25 text-charcoal/60 hover:border-blaze"}`} data-testid="firearm-cat-all">All</button>
          {(data?.categories || []).map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${cat === c ? "bg-blaze text-white border-blaze" : "border-saddle/25 text-charcoal/60 hover:border-blaze"}`} data-testid={`firearm-cat-${c.toLowerCase()}`}>{c}</button>
          ))}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search manufacturer, model, caliber…" className="w-full border border-saddle/20 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-forest" data-testid="firearm-search" />
          </div>
        </div>

        {data === null ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : list.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-saddle/30" data-testid="firearms-empty">
            <Crosshair size={34} className="mx-auto text-saddle/60" />
            <p className="font-serif text-2xl text-forest mt-4">Firearms research is being built.</p>
            <p className="text-charcoal/60 mt-2 max-w-md mx-auto">We only publish verified manufacturer specifications. Research pages are coming soon — nothing fabricated.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="firearms-grid">
            {list.map((f, i) => (
              <motion.button key={f.id} onClick={() => navigate(`/firearms/${f.id}`)}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.03 }}
                className="group bg-white border border-saddle/20 text-left hover:border-forest transition-colors overflow-hidden" data-testid={`firearm-card-${f.id}`}>
                <div className="h-44 bg-charcoal overflow-hidden">
                  {f.image_url ? <img src={f.image_url} alt={`${f.manufacturer} ${f.model}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    : <div className="w-full h-full grid place-items-center"><Crosshair size={30} className="text-bone/30" /></div>}
                </div>
                <div className="p-4">
                  <span className="hr-overline text-saddle text-[10px]">{f.category}{f.caliber ? ` · ${f.caliber}` : ""}</span>
                  <h3 className="font-serif text-xl text-forest mt-1">{f.manufacturer} {f.model}</h3>
                  {f.action && <p className="text-xs text-charcoal/55 mt-1">{f.action}{f.barrel_length ? ` · ${f.barrel_length}` : ""}</p>}
                  <span className="inline-flex items-center gap-1.5 text-blaze text-sm font-semibold mt-3">View research <ArrowRight size={14} /></span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </main>
      <PolicyBar />
      <Footer />
    </div>
  );
}

export function FirearmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.publicFirearm(id).then(setF).catch(() => setErr(true)); }, [id]);

  const specs = useMemo(() => (f ? SPEC_ROWS.filter(([k]) => f[k]) : []), [f]);

  if (err) return (
    <div className="bg-paper min-h-screen"><SiteHeader />
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="firearm-notfound">
        <p className="font-serif text-3xl text-forest">Research page not found.</p>
        <Link to="/firearms" className="text-blaze font-semibold mt-4 inline-block">Back to Firearms Research</Link>
      </div><Footer />
    </div>
  );
  if (!f) return <div className="bg-paper min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></div>;

  return (
    <div className="bg-paper min-h-screen" data-testid="firearm-detail">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-10 py-10">
        <button onClick={() => navigate("/firearms")} className="flex w-fit items-center gap-1.5 text-sm text-saddle hover:text-forest mb-6" data-testid="firearm-back"><ArrowLeft size={15} /> All firearms research</button>
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="bg-charcoal h-72 lg:h-96 overflow-hidden grid place-items-center">
            {f.image_url ? <img src={f.image_url} alt={`${f.manufacturer} ${f.model}`} className="w-full h-full object-cover" /> : <Crosshair size={48} className="text-bone/25" />}
          </div>
          <div>
            <span className="hr-overline text-saddle">{f.category}</span>
            <h1 className="font-serif text-4xl sm:text-5xl text-forest tracking-tight mt-2">{f.manufacturer} {f.model}</h1>
            {f.hunting_applications && (
              <div className="mt-4"><span className="text-[11px] uppercase tracking-wide text-charcoal/50">Fit for hunt</span>
                <p className="text-charcoal/80 mt-1">{f.hunting_applications}</p></div>
            )}
            <div className="flex flex-wrap gap-3 mt-6">
              {f.seller_url && <a href={f.seller_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-blaze text-white px-5 py-3 text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="firearm-seller">View at licensed seller <ExternalLink size={14} /></a>}
              {f.manufacturer_url && <a href={f.manufacturer_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-saddle/30 text-forest px-5 py-3 text-sm font-semibold hover:border-forest transition-colors" data-testid="firearm-manufacturer">Manufacturer <ExternalLink size={14} /></a>}
            </div>
            <p className="text-xs text-charcoal/45 mt-3">HomeRange does not sell firearms. Any purchase is completed independently with a licensed seller, subject to applicable laws and eligibility.</p>
          </div>
        </div>

        {specs.length > 0 && (
          <section className="mt-10">
            <h2 className="font-serif text-2xl text-forest mb-4">Specifications</h2>
            <div className="bg-white border border-saddle/20 divide-y divide-saddle/10" data-testid="firearm-specs">
              {specs.map(([k, label]) => (
                <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
                  <span className="text-charcoal/55">{label}</span>
                  <span className="text-forest font-medium text-right">{f[k]}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {f.regulatory_notes && (
          <section className="mt-8 bg-white border border-saddle/20 p-5">
            <h2 className="font-serif text-xl text-forest">Regulations</h2>
            <p className="text-charcoal/70 text-sm mt-2">{f.regulatory_notes}</p>
          </section>
        )}

        {f.source && <p className="text-xs text-charcoal/45 mt-6">Specs source: {f.source}</p>}
      </main>
      <PolicyBar />
      <Footer />
    </div>
  );
}
